import { isObservableArray } from 'mobx'
import { types } from 'mobx-state-tree'

import { CHANGE_ICON } from '../ActivityFeedIcon'
import { ReferenceFreightieOrUser } from '../Actors'
import { TAvatarType } from '../avatars/AvatarType'
import { getAvatar, getAvatarType, getTitleFromActor } from '../avatars/avatarUtils'
import { IActivityMessageSection, IActivityTitle, LABELED, UPDATE_TABLE } from '../messages/MessageSectionType'
import { Activity } from './Activity'

const productUpdateActionTranslationMap = {
  updated: 'activity_log-product_update-action_updated',
  deleted: 'activity_log-product_update-action_deleted',
}

const fieldsNameTranslationMap = {
  name: {
    label: 'activity_log-product_update-name',
  },
  skucode: {
    label: 'activity_log-product_update-sku_code',
  },
  hscodes: {
    label: 'activity_log-product_update-hs_code',
  },
  notes: {
    label: 'activity_log-product_update-notes',
  },
}

export const MAX_FIELD_TEXT_LENGTH = 85
export const LONG_TEXT_ENDING = '...'

const ChangedFields = types.map(types.union(types.string, types.array(types.string)))

const shortenText = (value: string): string => {
  return value.length > MAX_FIELD_TEXT_LENGTH
    ? value
        .substring(0, MAX_FIELD_TEXT_LENGTH - LONG_TEXT_ENDING.length)
        .trim()
        .concat(LONG_TEXT_ENDING)
    : value
}

export const ProductUpdateActivity = types
  .compose(
    'ProductUpdateActivity',
    Activity,
    types.model({
      action: types.string,
      productId: types.string,
      productName: types.string,
      actor: ReferenceFreightieOrUser,
      changedFields: ChangedFields,
    })
  )
  .preProcessSnapshot((snapshot) => {
    const { freightieAuthor_id, userAuthor_id } = snapshot
    return {
      ...snapshot,
      actor: freightieAuthor_id || userAuthor_id,
    }
  })
  .views((self) => ({
    get hasContentBody(): boolean {
      if (self.action === 'deleted') {
        return false
      }

      return true
    },
    getRowText(key: string, value: string): string[] {
      const fieldName = fieldsNameTranslationMap[key]
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- TODO: Fix template expression
      return [`${fieldName.label}`, shortenText(value)]
    },
  }))
  .views((self) => ({
    get tableTitle(): IActivityMessageSection {
      return {
        type: LABELED,
        style: { display: 'block' },
        label: productUpdateActionTranslationMap[self.action],
        content: shortenText(self.productName),
        translate: true,
      }
    },
    get content(): IActivityMessageSection {
      const changedFields = self.changedFields.entries()
      const tableContent = changedFields.map(([key, value]) => {
        const parsedValue = isObservableArray(value) ? value.peek().join(', ') : value
        return self.getRowText(key, parsedValue)
      })
      return {
        type: UPDATE_TABLE,
        tableContent,
        style: { marginRight: '7px' },
        translate: true,
      }
    },
  }))
  .views((self) => ({
    get activityIcon(): string {
      return CHANGE_ICON
    },
    get avatarType(): TAvatarType {
      return getAvatarType(self.actor)
    },
    get avatar(): string {
      return getAvatar(self.actor)
    },
    get messageContent(): IActivityMessageSection[] {
      const messageContent = [self.tableTitle]
      self.hasContentBody && messageContent.push(self.content)
      return messageContent
    },
    get title(): IActivityTitle {
      return getTitleFromActor(self.actor)
    },
  }))

export type TProductUpdateActivity = typeof ProductUpdateActivity.Type
